import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "./useAuth";

export function useApi<T = unknown>(
  url: string | null,
  options?: { skip?: boolean },
) {
  const { getHeaders } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasData = useRef(false);

  const fetchData = useCallback(async () => {
    if (!url || options?.skip) return;
    // Only show loading spinner on initial fetch (no data yet),
    // so refetch preserves scroll position and list state
    if (!hasData.current) setLoading(true);
    setError(null);
    try {
      const res = await fetch(url, { headers: getHeaders() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json);
      hasData.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [url, getHeaders, options?.skip]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useApiMutation<TBody = unknown, TResult = unknown>(url: string) {
  const { getHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (body: TBody, method: "POST" | "PUT" | "DELETE" = "POST"): Promise<TResult | null> => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(url, {
          method,
          headers: getHeaders(),
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const json = await res.json().catch(() => ({}));
          throw new Error(json.error || `HTTP ${res.status}`);
        }
        return (await res.json()) as TResult;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unknown error";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [url, getHeaders],
  );

  return { mutate, loading, error };
}
